<template>
  <Drawer :on-close="closePanel">
    <template #content>
      <div class="w-full flex gap-3 md:gap-6 flex-col !px-3 md:!px-0 !pb-3">
        <div class="bg-shape rounded-lg !p-0 mt-6 hidden md:flex felx-col gap-3 items-center">
          <template v-if="store.isMagicBrushEnabled">
            <div class="rounded-lg h-24 w-24 shrink-0 overflow-hidden">
              <video :src="imgEraseRestore" class="rounded-lg" width="100" height="100" :autoplay="true" :loop="true" :muted="true" :controls="false" webkit-playsinline playsinline />
            </div>
            <div class="flex flex-col gap-1 pr-2">
              <p class="text-xs font-bold text-typo-tertiary !m-0">{{ I18n.t("editor.magic_brush") }}</p>
              <p class="text-sm text-typo !m-0">{{ I18n.t("editor.easily_erase_or_restore_anything") }}</p>
            </div>
          </template>

          <div v-else class="h-24 flex flex-col items-center justify-center w-full">
            <p class="text-sm text-typo !m-0">{{ I18n.t("editor.manually_erase_or_restore") }}</p>
          </div>
        </div>

        <div class="flex gap-2">
          <ToggleButton :selected="store.drawSettings.action === DrawAction.Erase" @click="enterEraseMode()">
            <RemoveIconCircleLarge class="hidden md:block" />
            <RemoveIconCircle class="md:hidden" />
            <span>{{ I18n.t("editor.erase") }}</span>
          </ToggleButton>

          <ToggleButton :selected="store.drawSettings.action === DrawAction.Restore" @click="enterRestoreMode()">
            <AddIconCircleLarge class="hidden md:block" />
            <AddIconCircle class="md:hidden" />
            <span>{{ I18n.t("editor.restore") }}</span>
          </ToggleButton>
        </div>

        <div @mousedown="handleMouseDown" @mouseup="handleMouseUp" @touchstart="handleMouseDown" @touchend="handleMouseUp">
          <RangeSlider inline-label :label="I18n.t('editor.brush_size')" :value="sliderPosition" @update="updateBrushSize" />
        </div>

        <div class="flex items-center gap-2">
          <Toggle :name="I18n.t('editor.magic_brush')" :model-value="store.isMagicBrushEnabled" @update:modelValue="updateBrushMode" />
          <span class="text-sm text-typo">{{ I18n.t("editor.magic_brush") }}</span>
        </div>
      </div>
    </template>

    <template #mobile-actions>
      <TextButton variant="secondary" @click="reset">{{ I18n.t("editor.reset") }}</TextButton>
      <TextButton @click="closePanel">{{ I18n.t("editor.done") }}</TextButton>
    </template>

    <template #actions>
      <Button :full-width="true" variant="secondary" @click="reset">{{ I18n.t("editor.reset") }}</Button>
      <Button :full-width="true" @click="closePanel">{{ I18n.t("editor.done") }}</Button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import "@/src/i18n";

import { ref, computed, inject } from "vue";

import { Drawer, RangeSlider, Button, Toggle, ToggleButton, TextButton, AddIconCircle, AddIconCircleLarge, RemoveIconCircle, RemoveIconCircleLarge } from "prism";

import imgErase from "@assets/images/erase.mp4";
import imgRestore from "@assets/images/restore.mp4";

import { useEditorStore } from "@/stores/editor_store";
import { DrawAction } from "@/stores/line";
import type { Line, Point } from "@/stores/line";

const I18n = inject("I18n");

const store = useEditorStore();

interface EraseEstoreProps {
  editor: any;
}

const props = defineProps<EraseEstoreProps>();

const maxBrushSize: number = 200;
const minBrushSize: number = 20;
const brushSize = ref<number>(120);

const sliderPosition = computed(() => {
  return ((brushSize.value - minBrushSize) / (maxBrushSize - minBrushSize)) * 100;
});

const handleMouseDown = () => {
  store.isHoveringBrushSizeSlider = true;
  store.cursorPosition = calculateCursorPosition();
};

const handleMouseUp = () => {
  store.isHoveringBrushSizeSlider = false;
};

const updateBrushSize = (value: number) => {
  const newBrushSize = Math.round((value / 100) * (maxBrushSize - minBrushSize) + minBrushSize);
  brushSize.value = newBrushSize;
  store.drawSettings.brushSize = newBrushSize;
  store.cursorPosition = calculateCursorPosition();
};

const updateBrushMode = (value: boolean) => {
  store.isMagicBrushEnabled = value;
};

const calculateCursorPosition = (): Point => {
  const node = props.editor;
  const box = node.getBoundingClientRect();
  const centerX = (box.left + box.right) / 2;
  const centerY = (box.top + box.bottom) / 2;

  const x = centerX - store.drawSettings.brushSize / 2;
  const y = centerY - store.drawSettings.brushSize / 2;
  return { x, y };
};

const imgEraseRestore = computed(() => (store.drawSettings.action === DrawAction.Erase ? imgErase : imgRestore));

const enterEraseMode = () => {
  store.enterEraseMode();
};

const enterRestoreMode = () => {
  store.enterRestoreMode();
};

const emit = defineEmits<{
  (e: "closePanel"): void;
  (e: "reset"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};

const reset = () => {
  emit("reset");
};
</script>
